import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { StreakService } from '../../services/StreakService';
import { XpService } from '../../services/XpService';
import { SoundEffectService } from '../../services/SoundEffectService';
import { LearningProgressService } from '../../services/LearningProgressService';
import { ChunkyButton, Confetti, StarRow, ShakeWidget, FloatingXpBadge } from '../../components/GameUI';

// Drag-or-tap sentence reconstruction game — port of the mobile app's
// lib/games/sentence_builder/sentence_builder_screen.dart. Reuses the same
// pointer-events drag mechanic as WordMatchGame.jsx (not native HTML5
// drag-and-drop, since that doesn't work reliably on touch), plus a
// threshold-based tap fallback: a chip that never moves past a small
// distance on release is treated as a tap-to-place rather than a drag.

const PRIMARY = '#4F46E5';
const ACCENT = '#7C3AED';
const TAP_THRESHOLD = 6;

function shuffle(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

function buildRound(pool) {
  const challenge = pool[Math.floor(Math.random() * pool.length)];
  const words = challenge.words.map((text, originalIndex) => ({ text, originalIndex }));
  return {
    challenge,
    bank: shuffle(words),
    slots: new Array(challenge.words.length).fill(null),
  };
}

function starsFor(hintsUsed, mistakes) {
  if (hintsUsed === 0 && mistakes === 0) return 3;
  if (hintsUsed + mistakes <= 1) return 2;
  return 1;
}

function BankChip({ text, dragging, innerRef, style, ...rest }) {
  return (
    <div
      ref={innerRef}
      style={{
        background: '#FFFFFF',
        borderColor: ACCENT,
        boxShadow: dragging ? '0 6px 14px rgba(0,0,0,0.18)' : undefined,
        touchAction: 'none',
        ...style,
      }}
      className="select-none rounded-xl border-2 px-3.5 py-2.5 text-center text-sm font-extrabold text-slate-900"
      {...rest}
    >
      {text}
    </div>
  );
}

export default function SentenceBuilderGame() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { state } = useLocation();
  const {
    pool = [],
    targetLang = { code: '', label: '', dir: 'ltr' },
    langPair = 'guest',
  } = state || {};

  const invalid = !pool.length;

  const [round, setRound] = useState(() => (invalid ? { challenge: null, bank: [], slots: [] } : buildRound(pool)));
  const { challenge, bank, slots } = round;

  const [hintsUsed, setHintsUsed] = useState(0);
  const [mistakes, setMistakes] = useState(0);
  const [done, setDone] = useState(false);
  const [wrongPulse, setWrongPulse] = useState(false);
  const [hasInteracted, setHasInteracted] = useState(false);
  const [dragId, setDragId] = useState(null);
  const [dragPos, setDragPos] = useState({ x: 0, y: 0 });
  const [hoverIndex, setHoverIndex] = useState(null);

  const rewardedRef = useRef(false);
  const mountedRef = useRef(true);
  const slotRefs = useRef({});

  useEffect(() => {
    if (invalid) navigate('/games', { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => () => { mountedRef.current = false; }, []);

  function dismissHint() {
    if (!hasInteracted) setHasInteracted(true);
  }

  function newRound() {
    rewardedRef.current = false;
    setHintsUsed(0);
    setMistakes(0);
    setDone(false);
    setWrongPulse(false);
    setRound(buildRound(pool));
  }

  function finish(finalHints, finalMistakes) {
    setDone(true);
    SoundEffectService.playCorrect();
    if (navigator.vibrate) navigator.vibrate(15);
    if (rewardedRef.current) return;
    rewardedRef.current = true;
    const stars = starsFor(finalHints, finalMistakes);
    const xp = challenge.words.length * 3 + (stars === 3 ? 10 : 0);
    StreakService.recordActivity();
    XpService.addXp(xp);
    LearningProgressService.record(langPair, 'grammar', 1, { activityId: `sentence:${challenge.id}` });
    if (stars === 3) SoundEffectService.playCelebration();
  }

  function check(slotsArg, hintsArg = hintsUsed, mistakesArg = mistakes) {
    const correct = slotsArg.every((w, i) => w && w.originalIndex === i);
    if (correct) {
      finish(hintsArg, mistakesArg);
      return;
    }
    setWrongPulse(true);
    SoundEffectService.playWrong();
    if (navigator.vibrate) navigator.vibrate(35);
    setTimeout(() => {
      if (!mountedRef.current) return;
      setMistakes((m) => m + 1);
      setWrongPulse(false);
      setRound((prev) => ({
        challenge: prev.challenge,
        bank: shuffle(slotsArg.filter(Boolean)),
        slots: new Array(prev.challenge.words.length).fill(null),
      }));
    }, 550);
  }

  function placeFromBank(word, slotIndex) {
    dismissHint();
    const target = slotIndex != null ? slotIndex : slots.indexOf(null);
    if (target < 0 || slots[target] != null) return;
    const newBank = bank.filter((w) => w.originalIndex !== word.originalIndex);
    const newSlots = [...slots];
    newSlots[target] = word;
    setRound({ challenge, bank: newBank, slots: newSlots });
    if (newBank.length === 0) check(newSlots);
  }

  function returnToBank(slotIndex) {
    const word = slots[slotIndex];
    if (!word) return;
    dismissHint();
    const newSlots = [...slots];
    newSlots[slotIndex] = null;
    setRound({ challenge, bank: [...bank, word], slots: newSlots });
  }

  function useHint() {
    const i = slots.indexOf(null);
    if (i < 0) return;
    const bankIndex = bank.findIndex((w) => w.originalIndex === i);
    let newBank = bank;
    const newSlots = [...slots];
    if (bankIndex >= 0) {
      newSlots[i] = bank[bankIndex];
      newBank = bank.filter((_, idx) => idx !== bankIndex);
    } else {
      const slotIndex = slots.findIndex((w) => w && w.originalIndex === i);
      if (slotIndex >= 0) {
        newSlots[i] = slots[slotIndex];
        newSlots[slotIndex] = null;
      }
    }
    const newHintsUsed = hintsUsed + 1;
    setHintsUsed(newHintsUsed);
    setRound({ challenge, bank: newBank, slots: newSlots });
    if (newBank.length === 0) check(newSlots, newHintsUsed, mistakes);
  }

  function findSlotAt(x, y) {
    for (const [idxStr, el] of Object.entries(slotRefs.current)) {
      if (!el) continue;
      const r = el.getBoundingClientRect();
      if (x >= r.left && x <= r.right && y >= r.top && y <= r.bottom) return Number(idxStr);
    }
    return null;
  }

  function startPress(word, e) {
    const point = e.touches ? e.touches[0] : e;
    const startX = point.clientX;
    const startY = point.clientY;
    let dragging = false;

    function onMove(ev) {
      const p = ev.touches ? ev.touches[0] : ev;
      if (!dragging && Math.hypot(p.clientX - startX, p.clientY - startY) > TAP_THRESHOLD) {
        dragging = true;
        dismissHint();
        setDragId(word.originalIndex);
      }
      if (dragging) {
        if (ev.cancelable) ev.preventDefault();
        setDragPos({ x: p.clientX, y: p.clientY });
        setHoverIndex(findSlotAt(p.clientX, p.clientY));
      }
    }
    function onUp(ev) {
      const p = ev.changedTouches ? ev.changedTouches[0] : ev;
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('touchmove', onMove);
      window.removeEventListener('touchend', onUp);
      if (dragging) {
        const idx = findSlotAt(p.clientX, p.clientY);
        setDragId(null);
        setHoverIndex(null);
        if (idx != null) placeFromBank(word, idx);
      } else {
        placeFromBank(word);
      }
    }
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('touchmove', onMove, { passive: false });
    window.addEventListener('touchend', onUp);
  }

  const draggingWord = useMemo(() => bank.find((w) => w.originalIndex === dragId), [bank, dragId]);
  const stars = starsFor(hintsUsed, mistakes);
  const xpEarned = challenge ? challenge.words.length * 3 + (stars === 3 ? 10 : 0) : 0;

  if (invalid || !challenge) return null;

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="mx-auto max-w-lg px-4 py-4 sm:px-6">
        <div className="mb-4 flex items-center justify-between">
          <button
            onClick={() => navigate(-1)}
            className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-bold text-slate-500 shadow-sm hover:bg-slate-50"
          >
            ← {t('games.title')}
          </button>
          <h1 className="text-sm font-black text-slate-900">{t('games.sentenceBuilder', 'Sentence Builder')}</h1>
          {!done ? (
            <button
              onClick={useHint}
              disabled={bank.length === 0}
              className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs font-bold text-amber-700 shadow-sm hover:bg-amber-100 disabled:cursor-not-allowed disabled:opacity-40"
            >
              💡 {t('games.sentenceBuilderHint', 'Hint')}
            </button>
          ) : (
            <span className="w-16" />
          )}
        </div>

        {done ? (
          <div className="relative flex flex-col items-center py-10 text-center">
            {stars === 3 && <Confetti />}
            <span className="text-6xl">{stars === 3 ? '🏆' : '🎉'}</span>
            <h2 className="mt-3 text-2xl font-black text-slate-900">
              {t('games.sentenceBuilderComplete', 'Sentence Complete!')}
            </h2>
            <p dir={targetLang.dir} className="mt-2 text-center text-[15px] font-bold text-slate-500">
              {challenge.words.join(' ')}
            </p>
            <div className="mt-3">
              <StarRow count={stars} />
            </div>
            <div className="mt-3">
              <FloatingXpBadge xp={xpEarned} />
            </div>
            <div className="mt-8 w-full max-w-xs">
              <ChunkyButton color={PRIMARY} onClick={newRound} className="w-full py-4 text-center text-white">
                {t('games.tryAgain')}
              </ChunkyButton>
              <button
                onClick={() => navigate('/games')}
                className="mt-3 w-full rounded-2xl border border-slate-200 py-3 text-sm font-bold text-slate-600 hover:bg-slate-50"
              >
                {t('games.done')}
              </button>
            </div>
          </div>
        ) : (
          <div dir={targetLang.dir}>
            {!hasInteracted && (
              <div className="mb-3.5 flex items-center gap-2.5 rounded-2xl border border-indigo-100 bg-indigo-50 px-3.5 py-2.5">
                <span className="text-lg">🧩</span>
                <p className="text-xs font-bold text-indigo-700">
                  {t('games.sentenceBuilderIntro', 'Drag or tap the words into the right order.')}
                </p>
              </div>
            )}

            <ShakeWidget trigger={wrongPulse}>
              <div
                className="flex min-h-[64px] w-full flex-wrap gap-2 rounded-2xl p-2.5"
                style={{
                  background: '#F1F5F9',
                  border: `${wrongPulse ? 2 : 1}px solid ${wrongPulse ? '#FF4B4B' : '#E2E8F0'}`,
                }}
              >
                {slots.map((word, i) => (
                  <div
                    key={i}
                    ref={(el) => { slotRefs.current[i] = el; }}
                    onClick={() => word && returnToBank(i)}
                    className="flex h-11 min-w-[44px] items-center justify-center rounded-[10px] px-3 text-center text-sm font-extrabold transition-colors"
                    style={{
                      background: word ? 'rgba(79,70,229,0.12)' : 'transparent',
                      borderWidth: word ? 2 : 1.5,
                      borderStyle: 'solid',
                      borderColor: word ? PRIMARY : (hoverIndex === i ? PRIMARY : '#CBD5E1'),
                      color: PRIMARY,
                      cursor: word ? 'pointer' : 'default',
                    }}
                  >
                    {word ? word.text : <span className="inline-block w-5" />}
                  </div>
                ))}
              </div>
            </ShakeWidget>

            <div className="mt-6 flex flex-wrap justify-center gap-2.5">
              {bank.map((word) => (
                <div
                  key={word.originalIndex}
                  onPointerDown={(e) => startPress(word, e)}
                  onTouchStart={(e) => startPress(word, e)}
                  style={{ opacity: dragId === word.originalIndex ? 0.3 : 1, cursor: 'grab' }}
                >
                  <BankChip text={word.text} />
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {draggingWord && (
        <div
          className="pointer-events-none fixed z-50"
          style={{ left: dragPos.x - 60, top: dragPos.y - 24, width: 120 }}
        >
          <BankChip text={draggingWord.text} dragging />
        </div>
      )}
    </div>
  );
}
